import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { SERVER_URL } from "./config";
import "./InvitationResponse.css";

const I18N = {
  he: {
    dir: "rtl",
    hello: (name) => `שלום ${name || ""}`,
    invitedTo: (eventTitle, subjectName) =>
      `הנכם מוזמנים ל${eventTitle} של ${subjectName}`,
    hall: (hall) => ` ${hall}`,

    loadError: "שגיאה בטעינת ההזמנה. נסו שוב מאוחר יותר.",
    saveError: "שגיאה בשמירה. נסו שוב.",

    thanksYes: "תודה שאישרתם – מחכים לראותכם! ",
    thanksNo: "תודה על העדכון ",
    editAnswer: "שינוי תשובה",

    rsvpTitle: "אישור הגעה",
    arrivingYes: "מגיע/ה",
    arrivingNo: "לא מגיע/ה",

    howMany: "כמה תגיעו?",
    decOne: "הפחת אחד",
    incOne: "הוסף אחד",

    separatedTitle: "האירוע בהפרדה מלאה:",
    howManyBoys: "כמה בנים?",
    howManyGirls: "כמה בנות?",

    vegetarianHowMany: "כמה מנות צמחוניות?",
    kidsHowMany: (kidsAge) =>
      `כמה ילדים מגיעים${kidsAge === 0 ? "" : ` (עד גיל ${kidsAge})`}?`,

    // ✅ DRIVE – multi + single
    driveQuestionMulti: "יש הסעה מ: {list} — האם תרצו להגיע בהסעה?",
    driveQuestionSingle: (name) =>
      name
        ? `מתוכננת הסעה מ${name} — האם תרצו להגיע בהסעה?`
        : "האם תרצו להגיע בהסעה?",
    driveNo: "לא",
    driveYes: "כן",
    driveYesFrom: (name) => `כן מ${name}`,
    errChooseDrive: "אם תרצו להגיע בהסעה — חובה לבחור מאיפה.",

    notesLabel: "הערות נוספות? (עד 30 תווים)",
    notesPlaceholder: "כתבו כאן הערות קצרות…",

    send: "שלחו תשובה",
    sending: "שולח…",

    errChooseArriving: "אנא בחרו אם אתם מגיעים.",
    errGuestCount: "יש להזין כמה אנשים יגיעו (לפחות 1).",
    errNeedBoysOrGirls: "יש למלא לפחות מספר בנים או מספר בנות.",
    errBoysGirlsMustMatch: "סה״כ בנים + בנות חייב להיות שווה למספר המגיעים.",
    errVegInvalid: "מנות צמחוניות: מספר בין 0 לסה״כ המגיעים.",
    errKidsInvalid: "מספר הילדים: בין 0 לסה״כ המגיעים.",

    copyAutoFail:
      "לא הצלחנו להעתיק את הקישור באופן אוטומטי.\nנסו ללחוץ לחיצה ארוכה על הקישור ולהעתיק אותו ידנית.",
    copiedOk:
      "הקישור הועתק.\nפתחו דפדפן (Chrome / Safari), הדביקו את הקישור בשורת הכתובת ולחצו אנטר.",
    copyFail:
      "לא הצלחנו להעתיק את הקישור.\nנסו ללחוץ לחיצה ארוכה על הקישור ולהעתיק אותו ידנית.",

    EVENT_LABELS_TITLE: {
      wedding: "חתונה",
      bat_mitzva: "בת מצווה",
      bar_mitzva: "בר מצווה",
      hinna: "חינה",
      brit: "ברית",
      brita: "בריתה",
    },
    EVENT_LABELS_TEXT: {
      wedding: "החתונה",
      bat_mitzva: "בת המצווה",
      bar_mitzva: "בר המצווה",
      hinna: "החינה",
      brit: "הברית",
      brita: "הבריתה",
    },

    fallbackEventTitle: "אירוע",
    fallbackEventText: "האירוע",
    joinCouple: (a, b) => [a, b].filter(Boolean).join(" ו"),
  },

  en: {
    dir: "ltr",
    hello: (name) => `Hello ${name || ""}`,
    invitedTo: (eventTitle, subjectName) =>
      `You are invited to the ${eventTitle} of ${subjectName}`,
    hall: (hall) => `Hall ${hall}`,

    loadError: "Error loading the invitation. Please try again later.",
    saveError: "Error saving. Please try again.",

    thanksYes: "Thanks for confirming — can't wait to see you! 🎉",
    thanksNo: "Thanks for letting us know 🙏",
    editAnswer: "Edit response",

    rsvpTitle: "RSVP",
    arrivingYes: "Attending",
    arrivingNo: "Not attending",

    howMany: "How many guests?",
    decOne: "Decrease",
    incOne: "Increase",

    separatedTitle: "Separate seating event:",
    howManyBoys: "How many boys?",
    howManyGirls: "How many girls?",

    vegetarianHowMany: "How many vegetarian meals?",
    kidsHowMany: (kidsAge) =>
      `How many children${kidsAge === 0 ? "" : ` (up to age ${kidsAge})`}?`,

    // ✅ DRIVE – multi + single
    driveQuestionMulti: "Shuttle options: {list} — will you come by shuttle?",
    driveQuestionSingle: (name) =>
      name
        ? `A shuttle is planned from ${name} — will you come by shuttle?`
        : "Will you come by shuttle?",
    driveNo: "No",
    driveYes: "Yes",
    driveYesFrom: (name) => `Yes, from ${name}`,
    errChooseDrive: "If you choose shuttle — please select where from.",

    notesLabel: "Any notes? (up to 30 characters)",
    notesPlaceholder: "Write a short note…",

    send: "Send response",
    sending: "Sending…",

    errChooseArriving: "Please choose whether you are attending.",
    errGuestCount: "Please enter how many people are coming (at least 1).",
    errNeedBoysOrGirls: "Please enter at least boys or girls.",
    errBoysGirlsMustMatch: "Boys + Girls must equal the total number of guests.",
    errVegInvalid: "Vegetarian meals must be between 0 and the total guests.",
    errKidsInvalid: "Children count must be between 0 and total guests.",

    copyAutoFail:
      "We couldn't copy the link automatically.\nTry long-pressing the link and copying it manually.",
    copiedOk:
      "Link copied.\nOpen a browser (Chrome / Safari), paste the link in the address bar, and press Enter.",
    copyFail:
      "We couldn't copy the link.\nTry long-pressing the link and copying it manually.",

    EVENT_LABELS_TITLE: {
      wedding: "Wedding",
      bat_mitzva: "Bat Mitzvah",
      bar_mitzva: "Bar Mitzvah",
      hinna: "Henna",
      brit: "Brit Mila",
      brita: "Brita",
    },
    EVENT_LABELS_TEXT: {
      wedding: "the wedding",
      bat_mitzva: "the bat mitzvah",
      bar_mitzva: "the bar mitzvah",
      hinna: "the henna",
      brit: "the brit mila",
      brita: "the brita",
    },

    fallbackEventTitle: "Event",
    fallbackEventText: "the event",
    joinCouple: (a, b) => [a, b].filter(Boolean).join(" & "),
  },

  fr: {
    dir: "ltr",
    hello: (name) => `Bonjour ${name || ""}`,
    invitedTo: (eventTitle, subjectName) =>
      `Vous êtes invité(e) ${eventTitle} de ${subjectName}`,
    hall: (hall) => `Salle ${hall}`,

    loadError: "Erreur lors du chargement de l’invitation. Réessayez plus tard.",
    saveError: "Erreur lors de l’enregistrement. Réessayez.",

    thanksYes: "Merci pour votre confirmation — hâte de vous voir ! 🎉",
    thanksNo: "Merci pour votre réponse 🙏",
    editAnswer: "Modifier la réponse",

    rsvpTitle: "Confirmation de présence",
    arrivingYes: "Présent(e)",
    arrivingNo: "Absent(e)",

    howMany: "Combien serez-vous ?",
    decOne: "Diminuer",
    incOne: "Augmenter",

    separatedTitle: "Événement avec séparation :",
    howManyBoys: "Combien de garçons ?",
    howManyGirls: "Combien de filles ?",

    vegetarianHowMany: "Combien de repas végétariens ?",
    kidsHowMany: (kidsAge) =>
      `Combien d’enfants${kidsAge === 0 ? "" : ` (jusqu’à ${kidsAge} ans)`} ?`,

    // ✅ DRIVE – multi + single
    driveQuestionMulti: "Navettes: {list} — viendrez-vous en navette ?",
    driveQuestionSingle: (name) =>
      name
        ? `Une navette est prévue depuis ${name} — viendrez-vous en navette ?`
        : "Viendrez-vous en navette ?",
    driveNo: "Non",
    driveYes: "Oui",
    driveYesFrom: (name) => `Oui, depuis ${name}`,
    errChooseDrive: "Si vous choisissez la navette — sélectionnez le départ.",

    notesLabel: "Un petit mot ? (30 caractères max)",
    notesPlaceholder: "Écrivez un petit mot...",

    send: "Envoyer",
    sending: "Envoi…",

    errChooseArriving: "Veuillez indiquer si vous venez.",
    errGuestCount: "Veuillez indiquer le nombre de personnes (au moins 1).",
    errNeedBoysOrGirls: "Veuillez renseigner au moins un garçon ou une fille.",
    errBoysGirlsMustMatch:
      "Garçons + Filles doit être égal au nombre total de personnes.",
    errVegInvalid: "Repas végétariens : un nombre entre 0 et le total.",
    errKidsInvalid: "Nombre d’enfants : entre 0 et le total.",

    copyAutoFail:
      "Impossible de copier le lien automatiquement.\nEssayez d’appuyer longuement sur le lien pour le copier manuellement.",
    copiedOk:
      "Lien copié.\nOuvrez un navigateur (Chrome / Safari), collez le lien dans la barre d’adresse puis appuyez sur Entrée.",
    copyFail:
      "Impossible de copier le lien.\nEssayez d’appuyer longuement sur le lien pour le copier manuellement.",

    EVENT_LABELS_TITLE: {
      wedding: "au mariage",
      bat_mitzva: "à la bat mitzvah",
      bar_mitzva: "à la bar mitzvah",
      hinna: "au henné",
      brit: "à la brit mila",
      brita: "à la brita",
    },
    EVENT_LABELS_TEXT: {
      wedding: "le mariage",
      bat_mitzva: "la bat mitzvah",
      bar_mitzva: "la bar mitzvah",
      hinna: "le henné",
      brit: "la brit mila",
      brita: "la brita",
    },

    fallbackEventTitle: "à l’événement",
    fallbackEventText: "l’événement",
    joinCouple: (a, b) => [a, b].filter(Boolean).join(" et "),
  },
};

const normalizeLang = (lang) => {
  const v = String(lang || "he").toLowerCase().trim();
  if (v === "he" || v === "en" || v === "fr") return v;
  return "he";
};

const InvitationResponse = ({ lang = "he" }) => {
  const { uuid } = useParams();

  const safeLang = normalizeLang(lang);
  const t = I18N[safeLang];

  const [invited, setInvited] = useState({});
  const [loading, setLoading] = useState(true);

  const [arriving, setArriving] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [command, setCommand] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [boysCount, setBoysCount] = useState("");
  const [girlsCount, setGirlsCount] = useState("");

  const [askKids, setAskKids] = useState(true);
  const [askVegetarian, setAskVegetarian] = useState(true);
  const [kidsAge, setKidsAge] = useState(10);

  // ✅ DRIVE (NEW BACKEND)
  const [askDrive, setAskDrive] = useState(false);
  const [shuttleOptions, setShuttleOptions] = useState([]); // [{id,name}]
  const [shuttleChoice, setShuttleChoice] = useState("no"); // "no" | "<id>"

  const [kidsCountTotal, setKidsCountTotal] = useState("");
  const [vegetarianCountTotal, setVegetarianCountTotal] = useState("");

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isSeparated = invited?.is_separated_event;

  const SINGLE_NAME_EVENTS = useMemo(
    () => new Set(["bat_mitzva", "bar_mitzva", "brit", "brita"]),
    []
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const normalizeUrl = (url) => {
    if (!url) return "";
    const tt = url.trim();
    if (/^https?:\/\//i.test(tt)) return tt;
    return `https://${tt}`;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${SERVER_URL}invitation/confirm/${uuid}/`, {
          params: safeLang !== "he" ? { lang: safeLang } : undefined,
        });
        if (!alive) return;

        const data = res.data || {};
        setInvited(data);

        setArriving(
          data.arriving === null || data.arriving === undefined
            ? ""
            : Number(data.arriving) > 0
            ? "1"
            : "0"
        );
        if (Number(data.arriving) > 0) setGuestCount(String(data.arriving));

        setAskKids(data.ask_kids ?? true);
        setAskVegetarian(data.ask_vegetarian ?? true);
        setKidsAge(data.kids_age ?? 10);

        // ✅ DRIVE: from backend
        const backendAskDrive = !!data.ask_drive;
        setAskDrive(backendAskDrive);

        const optsRaw = Array.isArray(data.shuttle_options) ? data.shuttle_options : [];
        const opts = optsRaw
          .map((x) => ({
            id: x?.id,
            name: String(x?.name || "").trim(),
          }))
          .filter((x) => x.id != null && x.name);

        setShuttleOptions(opts);

        // if guest already chose a location before
        const chosenId = data.shuttle_location_id;
        if (backendAskDrive && chosenId != null) {
          setShuttleChoice(String(chosenId));
        } else {
          setShuttleChoice("no");
        }

        if (data.is_separated_event) {
          setBoysCount(data.boys_count ? String(data.boys_count) : "");
          setGirlsCount(data.girls_count ? String(data.girls_count) : "");
        }

        setKidsCountTotal(
          typeof data.kids_count === "number" ? String(data.kids_count) : ""
        );
        setVegetarianCountTotal(
          typeof data.vegetarian_count === "number"
            ? String(data.vegetarian_count)
            : ""
        );

        setIsEditing(data.arriving === null || data.arriving === undefined);
      } catch {
        setFormError(t.loadError);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [uuid, safeLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventType = invited?.event_type || "wedding";

  const EVENT_LABELS_TITLE = t.EVENT_LABELS_TITLE || I18N.he.EVENT_LABELS_TITLE;

  const eventTitle =
    EVENT_LABELS_TITLE[eventType] || t.fallbackEventTitle || "Event";

  const pickText = (...vals) => {
    for (const v of vals) {
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  };

  const groomDisplay =
    safeLang === "en"
      ? pickText(
          invited?.groom_name_en,
          invited?.groom_en,
          invited?.groomNameEn,
          invited?.groom_name,
          invited?.groom
        )
      : safeLang === "fr"
      ? pickText(
          invited?.groom_name_fr,
          invited?.groom_fr,
          invited?.groomNameFr,
          invited?.groom_name,
          invited?.groom
        )
      : pickText(invited?.groom_name, invited?.groom);

  const brideDisplay =
    safeLang === "en"
      ? pickText(
          invited?.bride_name_en,
          invited?.bride_en,
          invited?.brideNameEn,
          invited?.bride_name,
          invited?.bride
        )
      : safeLang === "fr"
      ? pickText(
          invited?.bride_name_fr,
          invited?.bride_fr,
          invited?.brideNameFr,
          invited?.bride_name,
          invited?.bride
        )
      : pickText(invited?.bride_name, invited?.bride);

  const hallDisplay =
    safeLang === "en"
      ? pickText(invited?.hall_en, invited?.hallEn, invited?.hall)
      : safeLang === "fr"
      ? pickText(invited?.hall_fr, invited?.hallFr, invited?.hall)
      : pickText(invited?.hall);

  const hebrewDateDisplay =
    safeLang === "en"
      ? pickText(invited?.hebrew_date_en, invited?.hebrewDateEn, invited?.hebrew_date)
      : safeLang === "fr"
      ? pickText(invited?.hebrew_date_fr, invited?.hebrewDateFr, invited?.hebrew_date)
      : pickText(invited?.hebrew_date);

  const subjectName = SINGLE_NAME_EVENTS.has(eventType)
    ? groomDisplay || ""
    : t.joinCouple(groomDisplay, brideDisplay);

  const inc = (val, setter, min = 0) => {
    const n = Math.max(min, parseInt(val || 0, 10) + 1);
    setter(String(n));
  };

  const dec = (val, setter, min = 0) => {
    const n = Math.max(min, parseInt(val || 0, 10) - 1);
    setter(String(n));
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setFormError("");
  };

  const handleCopyLink = async (url) => {
    if (!url) return;

    const normalized = normalizeUrl(url);

    if (typeof navigator === "undefined" || !navigator.clipboard) {
      alert(t.copyAutoFail);
      return;
    }

    try {
      await navigator.clipboard.writeText(normalized);
      alert(t.copiedOk);
    } catch {
      alert(t.copyFail);
    }
  };

  // ✅ drive helpers
  const isSingleShuttle = askDrive && shuttleOptions.length === 1;
  const singleShuttleId = isSingleShuttle ? String(shuttleOptions[0].id) : null;
  const singleShuttleName = isSingleShuttle ? shuttleOptions[0].name : "";

  // For the question text
  const shuttleListForQuestion = useMemo(() => {
    if (!askDrive) return "";
    if (!Array.isArray(shuttleOptions) || shuttleOptions.length === 0) return "";
    return shuttleOptions.map((x) => x.name).join(", ");
  }, [askDrive, shuttleOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const going = arriving === "1";
    const total = going ? parseInt(guestCount || 0, 10) : 0;

    if (arriving !== "1" && arriving !== "0") {
      setFormError(t.errChooseArriving);
      return;
    }
    if (going && (!guestCount || total < 1)) {
      setFormError(t.errGuestCount);
      return;
    }
    if (isSeparated && going) {
      const b = parseInt(boysCount || 0, 10);
      const g = parseInt(girlsCount || 0, 10);
      if (b === 0 && g === 0) {
        setFormError(t.errNeedBoysOrGirls);
        return;
      }
      if (b + g !== total) {
        setFormError(t.errBoysGirlsMustMatch);
        return;
      }
    }
    if (going && askVegetarian) {
      const v = parseInt(vegetarianCountTotal || 0, 10);
      if (isNaN(v) || v < 0 || v > total) {
        setFormError(t.errVegInvalid);
        return;
      }
    }
    if (going && askKids) {
      const k = parseInt(kidsCountTotal || 0, 10);
      if (isNaN(k) || k < 0 || k > total) {
        setFormError(t.errKidsInvalid);
        return;
      }
    }

    // ✅ DRIVE validation:
    // allowed: "no" or one of ids
    if (going && askDrive && shuttleOptions.length > 0) {
      if (shuttleChoice !== "no") {
        const exists = shuttleOptions.some((x) => String(x.id) === String(shuttleChoice));
        if (!exists) {
          setFormError(t.errChooseDrive);
          return;
        }
      }
    }

    const payload = {
      arriving: going ? guestCount || "1" : "0",
      command,
    };

    if (going) {
      if (isSeparated) {
        payload.boys_count = boysCount || 0;
        payload.girls_count = girlsCount || 0;
      }
      if (askVegetarian) payload.vegetarian_count = vegetarianCountTotal || 0;
      if (askKids) payload.kids_count = kidsCountTotal || 0;

      if (askDrive) {
        // "no" or numeric id
        payload.drive_choice = shuttleChoice === "no" ? "no" : Number(shuttleChoice);
      }
    } else {
      // not coming => reset shuttle choice to "no"
      // (no need to send drive_choice)
    }

    try {
      setSubmitting(true);

      await axios.post(`${SERVER_URL}invitation/confirm/${uuid}/`, payload, {
        params: safeLang !== "he" ? { lang: safeLang } : undefined,
      });

      // refresh
      const res2 = await axios.get(`${SERVER_URL}invitation/confirm/${uuid}/`, {
        params: safeLang !== "he" ? { lang: safeLang } : undefined,
      });
      setInvited(res2.data);

      setIsEditing(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setFormError(t.saveError);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-container" dir={t.dir} translate="no">
        <div className="skeleton header" />
        <div className="skeleton line" />
        <div className="skeleton line" />
        <div className="skeleton card" />
      </div>
    );
  }

  return (
    <div className="invitation-container" translate="no" dir={t.dir} lang={safeLang}>
      <header className="hero">
        <div className="rings">{t.hello(invited?.name)}</div>
        <h1>{t.invitedTo(eventTitle, subjectName)}</h1>

        <p className="sub">
          {hallDisplay && <span className="chip">{t.hall(hallDisplay)}</span>}

          {invited.date && (
            <>
              <span className="chip">{formatDate(invited.date)}</span>
              {hebrewDateDisplay && <span className="chip">{hebrewDateDisplay}</span>}
            </>
          )}

          {invited.time && <span className="chip">{invited.time}</span>}
        </p>
      </header>

      {invited.command && <p className="something-to-say" style={{ whiteSpace: 'pre-wrap' }}>{invited.command}</p>}

      {!isEditing ? (
        <section className="response-summary">
          {Number(invited.arriving) > 0 ? <h2>{t.thanksYes}</h2> : <h2>{t.thanksNo}</h2>}

          <button className="btn primary" onClick={handleEditClick}>
            {t.editAnswer}
          </button>

          </section>
      ) : (
        <form onSubmit={handleSubmit} className="response-form" noValidate>
          <h2>{t.rsvpTitle}</h2>

          {formError && (
            <div className="form-error" role="alert" aria-live="assertive">
              {formError}
            </div>
          )}

          <div className="segmented">
            <button
              type="button"
              className={`seg ${arriving === "1" ? "active" : ""}`}
              onClick={() => setArriving("1")}
            >
              {t.arrivingYes}
            </button>
            <button
              type="button"
              className={`seg ${arriving === "0" ? "active" : ""}`}
              onClick={() => {
                setArriving("0");
                setShuttleChoice("no");
              }}
            >
              {t.arrivingNo}
            </button>
          </div>

          {arriving === "1" && (
            <>
              <label className="field">
                <span>{t.howMany}</span>
                <div className="stepper">
                  <button
                    type="button"
                    onClick={() => dec(guestCount, setGuestCount, 1)}
                    aria-label={t.decOne}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => inc(guestCount, setGuestCount, 1)}
                    aria-label={t.incOne}
                  >
                    +
                  </button>
                </div>
              </label>

              {isSeparated && (
                <div className="card split-count">
                  <p className="hint">{t.separatedTitle}</p>
                  <div className="grid-2">
                    <label className="field">
                      <span>{t.howManyBoys}</span>
                      <div className="stepper">
                        <button type="button" onClick={() => dec(boysCount, setBoysCount, 0)}>
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={boysCount}
                          onChange={(e) => setBoysCount(e.target.value)}
                        />
                        <button type="button" onClick={() => inc(boysCount, setBoysCount, 0)}>
                          +
                        </button>
                      </div>
                    </label>

                    <label className="field">
                      <span>{t.howManyGirls}</span>
                      <div className="stepper">
                        <button type="button" onClick={() => dec(girlsCount, setGirlsCount, 0)}>
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={girlsCount}
                          onChange={(e) => setGirlsCount(e.target.value)}
                        />
                        <button type="button" onClick={() => inc(girlsCount, setGirlsCount, 0)}>
                          +
                        </button>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {askVegetarian && (
                <label className="field">
                  <span>{t.vegetarianHowMany}</span>
                  <div className="stepper">
                    <button
                      type="button"
                      onClick={() => dec(vegetarianCountTotal, setVegetarianCountTotal, 0)}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={vegetarianCountTotal}
                      onChange={(e) => setVegetarianCountTotal(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => inc(vegetarianCountTotal, setVegetarianCountTotal, 0)}
                    >
                      +
                    </button>
                  </div>
                </label>
              )}

              {askKids && (
                <label className="field">
                  <span>{t.kidsHowMany(kidsAge)}</span>
                  <div className="stepper">
                    <button type="button" onClick={() => dec(kidsCountTotal, setKidsCountTotal, 0)}>
                      −
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={kidsCountTotal}
                      onChange={(e) => setKidsCountTotal(e.target.value)}
                    />
                    <button type="button" onClick={() => inc(kidsCountTotal, setKidsCountTotal, 0)}>
                      +
                    </button>
                  </div>
                </label>
              )}

              {/* ✅ DRIVE UI:
                  - אם יש מיקום אחד => רק כן/לא
                  - אם יש כמה => לא + כן מX/Y/Z
               */}
              {askDrive && shuttleOptions.length > 0 && (
<div className="card drive-card" style={{ marginTop: 14 }}>
                  <p className="hint">
                    {isSingleShuttle
                      ? t.driveQuestionSingle(singleShuttleName)
                      : t.driveQuestionMulti.replace("{list}", shuttleListForQuestion)}
                  </p>

                  <div className="drive-choice" role="radiogroup" aria-label="drive_choice">
                    {/* לא */}
                    <button
                      type="button"
                      className={`choice-btn ${shuttleChoice === "no" ? "active" : ""}`}
                      onClick={() => setShuttleChoice("no")}
                      aria-pressed={shuttleChoice === "no"}
                    >
                      {t.driveNo}
                    </button>

                    {/* ✅ מיקום אחד: כן */}
                    {isSingleShuttle ? (
                      <button
                        type="button"
                        className={`choice-btn ${
                          String(shuttleChoice) === String(singleShuttleId) ? "active" : ""
                        }`}
                        onClick={() => setShuttleChoice(String(singleShuttleId))}
                        aria-pressed={String(shuttleChoice) === String(singleShuttleId)}
                      >
                        {t.driveYes}
                      </button>
                    ) : (
                      /* ✅ כמה מיקומים: כן מX/Y/Z */
                      shuttleOptions.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`choice-btn ${
                            shuttleChoice === String(opt.id) ? "active" : ""
                          }`}
                          onClick={() => setShuttleChoice(String(opt.id))}
                          aria-pressed={shuttleChoice === String(opt.id)}
                        >
                          {t.driveYesFrom(opt.name)}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <label className="field">
            <span>{t.notesLabel}</span>
            <textarea
              placeholder={t.notesPlaceholder}
              value={command}
              onChange={(e) => {
                if (e.target.value.length <= 30) setCommand(e.target.value);
              }}
              maxLength={30}
            />
            <div className="char-counter">{command.length}/30</div>
          </label>

          <div className="sticky-actions">
            <button className="btn primary wide" type="submit" disabled={submitting}>
              {submitting ? t.sending : t.send}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default InvitationResponse;
